import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search } from 'lucide-react';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Button } from '../../../shared/components/Button';
import { Can } from '../../../shared/components/Can';
import { EmptyState } from '../../../shared/components/EmptyState';
import { PageContainer } from '../../../shared/components/PageContainer';
import { Pagination } from '../../../shared/components/Pagination';
import { TextInput } from '../../../shared/components/TextInput';
import { FEATURES } from '../../../shared/lib/permissions';
import { BondStatus, mensagemDeErro } from '../api';
import { BOND_STATUS_LABELS, formatDate } from '../components/format';
import { BondStatusBadge } from '../components/StatusBadge';
import { useColaboradores } from '../hooks';
import { useEstruturas } from '../hooks/vidaFuncional';
import { usePermissions } from '../../../shared/hooks/usePermissions';

const DEFAULT_PAGE_SIZE = 25;

export default function ColaboradoresPage() {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [status, setStatus] = useState<BondStatus | ''>('');
  const [orgUnitId, setOrgUnitId] = useState('');
  const [jobPositionId, setJobPositionId] = useState('');
  const { can } = usePermissions();
  // Os filtros usam as listagens de cadastro, abertas a quem movimenta ou administra cadastros.
  const podeFiltrar = can(FEATURES.pessoasMovimentacoes, 'view') || can(FEATURES.masterDataAreas, 'view');
  const areas = useEstruturas('areas', podeFiltrar);
  const cargos = useEstruturas('cargos', podeFiltrar);
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
  const [offset, setOffset] = useState(0);

  // Debounce da busca (300ms), como nas demais listagens.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => setOffset(0), [debounced, status, orgUnitId, jobPositionId]);

  const lista = useColaboradores({
    search: debounced || undefined, status: status || undefined, orgUnitId: orgUnitId || undefined,
    jobPositionId: jobPositionId || undefined, limit, offset,
  });
  const items = lista.data?.data ?? [];
  const count = lista.data?.pagination.count ?? 0;

  return (
    <PageContainer
      title="Colaboradores"
      subtitle="Cadastro mestre: pessoas e vínculos com as empresas do grupo."
      breadcrumbs={[{ label: 'Pessoas' }, { label: 'Colaboradores' }]}
      action={
        <Can feature={FEATURES.pessoasColaboradores} action="create">
          <Link to="/pessoas/colaboradores/novo">
            <Button>
              <Plus className="mr-2 h-5 w-5" />
              Novo colaborador
            </Button>
          </Link>
        </Can>
      }
    >
      <div className="flex flex-wrap items-end gap-4">
        <div className="w-full max-w-md">
          <TextInput
            label="Buscar por nome, CPF ou matrícula"
            icon={<Search className="h-5 w-5" />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="min-w-[240px]">
          <AutocompleteSelect
            label="Situação do vínculo"
            options={[
              { value: '', label: 'Todas' },
              ...Object.entries(BOND_STATUS_LABELS).map(([value, label]) => ({ value, label })),
            ]}
            value={status}
            onChange={(v) => setStatus(v as BondStatus | '')}
          />
        </div>
        {podeFiltrar && (
          <>
            <div className="min-w-[220px]">
              <AutocompleteSelect label="Área" options={[{ value: '', label: 'Todas' }, ...(areas.data ?? []).map((a) => ({ value: a.id, label: a.name }))]}
                value={orgUnitId} onChange={setOrgUnitId} />
            </div>
            <div className="min-w-[220px]">
              <AutocompleteSelect label="Cargo" options={[{ value: '', label: 'Todos' }, ...(cargos.data ?? []).map((c) => ({ value: c.id, label: c.name }))]}
                value={jobPositionId} onChange={setJobPositionId} />
            </div>
          </>
        )}
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        {lista.isLoading ? (
          <p className="p-6 text-sm text-gray-500">Carregando colaboradores…</p>
        ) : lista.isError ? (
          <p className="p-6 text-sm text-red-600">{mensagemDeErro(lista.error, 'Não foi possível carregar os colaboradores.')}</p>
        ) : items.length === 0 ? (
          <EmptyState title="Nenhum colaborador encontrado" description="Ajuste a busca ou cadastre um novo colaborador." />
        ) : (
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-left text-xs font-medium uppercase text-gray-500">
              <tr>
                <th className="px-4 py-3">Matrícula</th>
                <th className="px-4 py-3">Nome</th>
                <th className="px-4 py-3">CPF</th>
                <th className="px-4 py-3">Área / cargo</th>
                <th className="px-4 py-3">Empresa</th>
                <th className="px-4 py-3">Vínculo</th>
                <th className="px-4 py-3">Admissão</th>
                <th className="px-4 py-3">Situação</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((p) => (
                <tr key={p.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs text-gray-600">{p.currentBond.internalRegistration}</td>
                  <td className="px-4 py-3">
                    <Link to={`/pessoas/colaboradores/${p.id}`} className="font-medium text-blue-700 hover:underline">
                      {p.socialName || p.name}
                    </Link>
                    {p.socialName && <p className="text-xs text-gray-500">{p.name}</p>}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-600">{p.cpf}</td>
                  <td className="px-4 py-3 text-gray-700">
                    {p.orgUnitName ?? '—'}
                    {p.jobPositionName && <p className="text-xs text-gray-500">{p.jobPositionName}</p>}
                  </td>
                  <td className="px-4 py-3 text-gray-700">{p.currentBond.companyName ?? '—'}</td>
                  <td className="px-4 py-3 uppercase text-gray-700">{p.currentBond.employmentType}</td>
                  <td className="px-4 py-3 text-gray-700">{formatDate(p.currentBond.admissionDate)}</td>
                  <td className="px-4 py-3"><BondStatusBadge status={p.currentBond.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Pagination
        offset={offset}
        limit={limit}
        count={count}
        itemLabel="colaborador"
        onChange={(next) => {
          setOffset(next.offset);
          setLimit(next.limit);
        }}
      />
    </PageContainer>
  );
}
